import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setCurrentExhibit } from '../store/slices/exhibitSlice';
import TcButton from '../components/TcButton';
import { QrCode } from 'lucide-react';
import TcPaper from '../components/TcPaper';
import profileDefault from '../assets/images/profile-picture-default.png';
import TcScanQrCodeBar from '../components/TcScanQrCodeBar';
import TcPopupR65 from '../components/TcPopupR65';
import { useDeviceDetection, useOrientation } from '../hooks/useDeviceDetection';
import { useFullscreen } from '../hooks/useFullscreen';
import DeviceDetectionTest from '../components/DeviceDetectionTest';
import FullscreenButton from '../components/FullscreenButton';
import { getCountryNameFromCode } from '../utils/constants';
import { debugReduxState, monitorReduxStateChanges } from '../utils/functions';

export default function Home() {
    const { isFullscreen, isSupported } = useFullscreen();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { visitorId, name, email, nationality, language, selfieUrl, hasCompletedRegistration } = useAppSelector(state => state.user);
    const { currentExhibit } = useAppSelector(state => state.exhibit);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const isUserRegistered = hasCompletedRegistration && name && email

    // Device detection
    const { isMobile, isTablet, orientation, screenWidth } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';

    // Reset the current exhibit to null in state
    if (currentExhibit) dispatch(setCurrentExhibit(null));

    // Handle register link click
    const handleRegisterClick = (e: React.MouseEvent) => {
        e.preventDefault();
        navigate('/register');
    };

    useEffect(() => {
        // Debug Redux state to identify unexpected slices
        if (process.env.NODE_ENV === 'development') {
            console.log('🏠 Home component mounted - analyzing Redux state...');
            debugReduxState();
            
            // Start monitoring for state changes
            const stopMonitoring = monitorReduxStateChanges();
            
            // Cleanup monitoring on unmount
            return () => {
                stopMonitoring();
            };
        }
    }, []);

    useEffect(() => {
        // Show popup after 1 second
        const showTimer = setTimeout(() => {
            setIsPopupOpen(true);
        }, 1000);

        // Auto-hide popup after 11 seconds total (1s delay + 10s visible)
        const hideTimer = setTimeout(() => {
            setIsPopupOpen(false);
        }, 11000);

        // Cleanup timers on unmount
        return () => {
            clearTimeout(showTimer);
            clearTimeout(hideTimer);
        };
    }, []);

    // Responsive image sizing based on device
    const getProfileImageClasses = () => {
        if (isMobile) {
            return 'w-40 h-40';
        } else if (isTablet) {
            return 'w-64 h-64';
        } else {
            return 'w-64 h-64';
        }
    };

    // If user has completed registration, show the "You're all set!" screen
    return (
        <div className={`
            main-content-wrapper 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'}
            !pb-20 
            bg-image-galaxy 
            unselectable 
            has-transition 
        `}>
            {/* Background overlay - behind popup but above main content */}
            <div className={`fixed top-0 bottom-0 left-0 right-0 bg-black z-[5] ${isPopupOpen ? 'opacity-40' : 'opacity-0'} has-transition pointer-events-none`}></div>
            
            {/* Title */}
            <TcPaper classNameStyle="mt-6 mb-6">
                <h1 className="main-content-title text-center">
                    {visitorId 
                        ? (name ? t('home.allSet.title') : t('home.returningVisitor.title')) 
                        : t('home.newVisitor.title')}
                </h1>
            </TcPaper>

            {/* Welcome Content */}
            <TcPaper classNameStyle="general-content mb-6 max-w-md mx-auto">
                {/* Profile Image with device-responsive sizing */}
                <div className="flex justify-center mb-6">
                    <div className={`
                        ${getProfileImageClasses()}
                        rounded-full overflow-hidden border-2 border-[var(--color-beige-bright)] shadow-lg has-transition`}>
                        <img
                            src={selfieUrl || profileDefault}
                            alt="Profile"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>

                {isUserRegistered 
                    ? (
                        <div>
                            <p dangerouslySetInnerHTML={{ __html: t('home.allSet.welcome', { name }) }} />
                            <p dangerouslySetInnerHTML={{ __html: t('home.allSet.greeting', { nationality: getCountryNameFromCode(nationality) === "--" ? (language === "zh" ? "您的家乡" : "your hometown") : getCountryNameFromCode(nationality) }) }} />
                            <p dangerouslySetInnerHTML={{ __html: t('home.allSet.profileSetup', { email }) }} />
                            <p dangerouslySetInnerHTML={{ __html: t('home.allSet.makeMemories') }} />
                        </div>
                    ) : (visitorId ? (
                            <div>
                                <p dangerouslySetInnerHTML={{ __html: t('home.returningVisitor.welcome') }} />
                                <p dangerouslySetInnerHTML={{ __html: t('home.returningVisitor.greeting') }} />
                                <p dangerouslySetInnerHTML={{ __html: t('home.returningVisitor.makeMemories') }} />
                            </div>
                        ) : (
                            <div>
                                <p dangerouslySetInnerHTML={{ __html: t('home.notRegistered.welcome') }} />
                                <p dangerouslySetInnerHTML={{ __html: t('home.notRegistered.greeting') }} />
                                <p>
                                    {t('home.notRegistered.wantToRegister.beforeLink')}{' '}
                                    <button 
                                        onClick={handleRegisterClick}
                                        className="text-[var(--color-beige-bright)] underline hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 font-inherit"
                                    >
                                        {t('home.notRegistered.wantToRegister.linkText')}
                                    </button>
                                    {' '}{t('home.notRegistered.wantToRegister.afterLink')}
                                </p>
                                <p dangerouslySetInnerHTML={{ __html: t('home.notRegistered.makeMemories') }} />
                            </div>
                        )
                    )
                }
            </TcPaper>

            {isPopupOpen && (
                <div className={`fixed top-0 bottom-0 left-0 right-0 z-[15] ${isPopupOpen ? 'opacity-100' : 'opacity-0'} has-transition`}>
                    <TcPopupR65
                        handleClose={() => setIsPopupOpen(false)}
                    >
                        {/* If fullscreen is supported, show the fullscreen button */}
                        {isSupported && !isFullscreen && (
                            <div className='flex flex-col items-center justify-center mt-2 mb-4 pb-6 border-b border-gray-900 border-dashed'>
                                <p className='leading-tight'>{t('common.fullscreenSupported')}</p>
                                <FullscreenButton 
                                    showLabel={true}
                                    enterFullscreenText={t('common.enterFullscreen')}
                                    exitFullscreenText={t('common.exitFullscreen')}
                                />
                            </div>
                        )}
                        
                        <div
                            className={`leading-tight text-lg`}
                            dangerouslySetInnerHTML={{ __html: t('home.allSet.scanToStart') }}
                        />
                    </TcPopupR65>
                </div>
            )}

            <TcScanQrCodeBar classNameStyle="fixed bottom-0 left-0 right-0 z-[20]" />
            
            {/* Development device detection test - TODO: remove in production */}
            {process.env.NODE_ENV === 'development' && <DeviceDetectionTest />}
        </div>
    );
}